import React, { useMemo, useCallback, useState } from 'react';
import { Clock, ChevronRight, Check, AlertTriangle, Trash2, Loader, Calendar } from 'lucide-react';
import PlatformLogo from 'components/ui/PlatformLogo';
import { patchRequest } from '../../api/api';

const TYPE_STYLES = {
  ESCALAR: 'bg-green-500/20 text-green-100 border-green-400/30',
  OPTIMIZAR: 'bg-blue-500/20 text-blue-100 border-blue-400/30',
  REDUCIR: 'bg-red-500/20 text-red-100 border-red-400/30',
};

const getDaysUntilExpiration = (expiresAt) => {
  if (!expiresAt) return null;
  try {
    const diff = new Date(expiresAt) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  } catch {
    return null;
  }
};

const STATUS_LABELS = {
  APPLIED: 'Aplicada',
  FAILED: 'Error',
  DISMISSED: 'Descartada',
};

const KanbanCard = ({
  card,
  columnKey,
  onClick,
  onRatingSuccess,
  animatingCardIds = {},
  layout = 'card',
}) => {
  /** Numeric suggestion IDs for API calls. Group cards use `kanban-grp-*` as `card.id`; never pass that to /api/suggestions/:id/... */
  const ratingIds = useMemo(() => {
    const out = [];
    const seen = new Set();
    const push = (raw) => {
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) return;
      if (seen.has(n)) return;
      seen.add(n);
      out.push(n);
    };
    if (Array.isArray(card?.source_ids)) {
      card.source_ids.forEach(push);
    }
    if (out.length === 0) {
      push(card?.id);
    }
    if (out.length === 0 && Array.isArray(card?.childCards)) {
      card.childCards.forEach((ch) => push(ch?.id));
    }
    return out;
  }, [card?.source_ids, card?.id, card?.childCards]);

  const canCallSuggestionApi = ratingIds.length > 0;

  /** Animación tras aplicar/descartar: el padre guarda estado por id de sugerencia (numérico), no por `kanban-grp-*`. */
  const animatingStatus = useMemo(() => {
    for (const rid of ratingIds) {
      const s = animatingCardIds[rid];
      if (s != null) return s;
    }
    return null;
  }, [ratingIds, animatingCardIds]);

  const statusLabel = animatingStatus ? STATUS_LABELS[animatingStatus] || null : null;

  const isExpired = columnKey === 'expiradas';
  const isDismissed = columnKey === 'descartadas';
  const isPending = columnKey === 'pendientes';
  const isApplied = columnKey === 'aplicadas';
  const platform = (card.platform || '').toLowerCase();
  const actionType = String(card?.action_type || '').trim().toUpperCase();
  const isRefreshCreative = actionType === 'REFRESH_CREATIVE';
  const platforms = platform.includes('google')
    ? ['google']
    : platform.includes('meta')
    ? ['meta']
    : [platform];
  const daysLeft = isPending ? getDaysUntilExpiration(card.expires_at) : null;
  const primaryCtaLabel =
    isRefreshCreative
      ? (platform.includes('google') ? 'Ver en Google' : 'Ver en Meta')
      : 'Aplicar';

  const isAnimatingApplied = animatingStatus === 'APPLIED';
  const isAnimatingFailed = animatingStatus === 'FAILED';
  const isAnimatingDismissed = animatingStatus === 'DISMISSED';
  const isAnimating = !!animatingStatus;

  const [discardLoading, setDiscardLoading] = useState(false);

  const handleApplyClick = useCallback(
    (e) => {
      e?.stopPropagation?.();
      if (!canCallSuggestionApi || isAnimating) return;
      onClick?.(card);
    },
    [canCallSuggestionApi, isAnimating, onClick, card]
  );

  const handleDiscardClick = useCallback(
    async (e) => {
      e?.stopPropagation?.();
      if (!canCallSuggestionApi) return;
      setDiscardLoading(true);
      try {
        if (ratingIds.length > 1) {
          await Promise.all(ratingIds.map((rid) => patchRequest(`/api/suggestions/${rid}/dismiss`, {})));
          ratingIds.forEach((rid) => onRatingSuccess?.(rid, false, { status: 'DISMISSED' }));
        } else if (ratingIds[0] != null) {
          await patchRequest(`/api/suggestions/${ratingIds[0]}/dismiss`, {});
          onRatingSuccess?.(ratingIds[0], false, { status: 'DISMISSED' });
        }
      } finally {
        setDiscardLoading(false);
      }
    },
    [ratingIds, onRatingSuccess, canCallSuggestionApi]
  );

  const ratingActionsEl =
    isPending && !isAnimating && canCallSuggestionApi ? (
      <div
        className="flex flex-col items-end gap-2 w-full min-w-0 sm:flex-row sm:items-center sm:justify-end sm:gap-1.5"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleApplyClick}
          className="inline-flex items-center justify-center rounded-lg border border-emerald-400/40 bg-emerald-500/20 px-3 py-1.5 text-xs font-semibold text-emerald-100 hover:bg-emerald-500/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50 shrink-0"
        >
          {primaryCtaLabel}
        </button>
        <button
          type="button"
          onClick={handleDiscardClick}
          disabled={discardLoading}
          className="inline-flex items-center justify-center rounded-lg border border-amber-400/45 bg-amber-500/15 p-1.5 text-amber-100 hover:bg-amber-500/25 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50 disabled:cursor-not-allowed disabled:opacity-60 shrink-0"
          title="Descartar sugerencia"
          aria-label="Descartar sugerencia"
        >
          {discardLoading ? (
            <Loader size={14} className="shrink-0 animate-spin" aria-hidden />
          ) : (
            <Trash2 size={14} className="shrink-0" aria-hidden />
          )}
        </button>
      </div>
    ) : null;

  const borderClasses = isAnimatingApplied
    ? 'border-green-400/50 bg-green-500/15 scale-[0.97] opacity-0 translate-x-4'
    : isAnimatingFailed
    ? 'border-red-400/50 bg-red-500/15 scale-[0.97]'
    : isAnimatingDismissed
    ? 'border-amber-400/50 bg-amber-500/15 scale-[0.97] opacity-0 translate-x-4'
    : isExpired || isDismissed
    ? 'border-white/15 bg-white/5 opacity-60 cursor-pointer hover:bg-white/8'
    : isApplied
    ? 'border-emerald-400/50 bg-emerald-500/15 ring-1 ring-emerald-400/25 shadow-[0_0_28px_-10px_rgba(52,211,153,0.45)] cursor-pointer hover:bg-emerald-500/22 hover:border-emerald-400/60 hover:ring-emerald-400/35'
    : 'border-white/15 bg-white/5 cursor-pointer hover:bg-white/8';

  const animationOverlay = isAnimating && (
    <div
      className={`absolute inset-0 flex items-center justify-center z-10 rounded-xl ${
        isAnimatingApplied
          ? 'bg-green-500/20 backdrop-blur-[2px]'
          : isAnimatingDismissed
          ? 'bg-amber-500/20 backdrop-blur-[2px]'
          : 'bg-red-500/20 backdrop-blur-[2px]'
      }`}
    >
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-sm">
        {isAnimatingApplied ? (
          <Check size={14} className="text-green-300 animate-bounce" />
        ) : isAnimatingDismissed ? (
          <Check size={14} className="text-amber-200 animate-bounce" />
        ) : (
          <AlertTriangle size={14} className="text-red-300 animate-pulse" />
        )}
        <span
          className={`text-xs font-semibold ${
            isAnimatingApplied ? 'text-green-200' : isAnimatingDismissed ? 'text-amber-100' : 'text-red-200'
          }`}
        >
          {statusLabel}
        </span>
      </div>
    </div>
  );

  if (layout === 'row') {
    // Listado: solo icono de plataforma + nombre de la sugerencia (badges, subtítulo y acciones: en vista tarjeta o en el modal).
    return (
      <div
        onClick={() => !isAnimating && onClick?.(card)}
        className={`rounded-xl border transition-all duration-500 px-3 py-2.5 sm:py-3 relative overflow-hidden flex items-center gap-2.5 min-h-[44px] ${borderClasses}`}
      >
        {animationOverlay}
        <span className="shrink-0" aria-hidden>
          <PlatformLogo plataformas={platforms} size={16} />
        </span>
        <p className="text-sm font-semibold text-white leading-snug line-clamp-2 min-w-0 flex-1">
          {card.action}
        </p>
      </div>
    );
  }

  return (
    <div
      onClick={() => !isAnimating && onClick?.(card)}
      className={`rounded-xl border transition-all duration-500 p-3 space-y-2 relative overflow-hidden ${borderClasses}`}
    >
      {animationOverlay}

      {/* Row 1: Platform + type badge + chevron */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <PlatformLogo plataformas={platforms} size={16} />
          <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${
              TYPE_STYLES[card.type] || TYPE_STYLES.OPTIMIZAR
            }`}
          >
            {card.type}
          </span>
          {card.suggestionCount > 1 && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium border border-white/20 bg-white/10 text-white/80">
              {card.suggestionCount} sugerencias
            </span>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-white/40 shrink-0" />
      </div>

      {/* Row 2: Action */}
      <p className="text-sm font-semibold text-white leading-tight line-clamp-2">
        {card.action}
      </p>

      {/* Row 3: Campaign name */}
      <p className="text-xs text-white/60 truncate">{card.campaign_name}</p>

      {/* Row 4: Expiration or status indicator */}
      <div className="flex items-center justify-between gap-2">
        {isPending && daysLeft !== null && (
          <span
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${
              daysLeft <= 1
                ? 'bg-red-500/20 border-red-400/30 text-red-200'
                : daysLeft <= 3
                ? 'bg-amber-500/20 border-amber-400/30 text-amber-200'
                : 'bg-white/10 border-white/15 text-white/60'
            }`}
          >
            <Clock size={10} />
            {daysLeft === 0 ? 'Hoy' : `${daysLeft}d`}
          </span>
        )}
        {isApplied && card.applied_at && (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-100">
            <Calendar size={10} className="text-emerald-200/90" aria-hidden />
            Aplicada el {new Date(card.applied_at).toLocaleDateString('es-ES')}
          </span>
        )}
        {!isPending && !isApplied && <span />}

        {ratingActionsEl}
      </div>
    </div>
  );
};

export default KanbanCard;
